import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import FundraisingBar from './FundraisingBar';
import DetailLearnMore from './Components/DetailLearnMore';
import DetailDescription from './Components/DetailDescription';
import {
  Button,
  FallbackHeaderImage,
  imageUrl,
  imageVersionFor,
  useBrandColors,
  LEGACY_UNBRANDED_FORM_COLOR,
} from '@charitystack/shared';
import { getGlobalImageUrl } from './config/env';
import { useIsHostedPage } from './hooks/useIsHostedPage';
import { SURFACE_CONTENT_CLASS } from './Util/pageTransition';
// formHeightUtils no longer needed — detail pages use CSS container-query heights

function EmbedFormDetailPageContent({
  data,
  setPage,
  generatedBrandColors: parentGeneratedBrandColors,
  isDrawerOpen,
  previewMode = false,
  formDisplayMode,
  headingLevel = 'h1',
}) {
  const paymentsUnavailable = !previewMode && data?.paymentsEnabled === false && data?.formType !== 'EVENT';
  const isOverlay = formDisplayMode === 'overlay';
  // Render the form title with the requested heading tag (defaults to <h1>). The
  // marketing-website demo passes 'h2' so this embedded title doesn't duplicate the
  // marketing page's own H1. Styling is driven entirely by className, so the tag is
  // purely semantic.
  const HeadingTag = headingLevel;
  const isHostedPage = useIsHostedPage();

  // NEW: Use shared brand colors hook - direct color management
  const [lightColor, setLightColor] = useState(data?.lightColor || LEGACY_UNBRANDED_FORM_COLOR); // CON-528: no colour is no brand, never sage
  const { brandColors: localBrandColors, primaryColor: localPrimaryColor } =
    useBrandColors(lightColor);

  // CRITICAL FIX: Use pre-generated brand colors from parent (EmbedForm) if available
  // This prevents flicker caused by useBrandColors initializing with empty state
  const hasParentBrandColors =
    parentGeneratedBrandColors && Object.keys(parentGeneratedBrandColors).length > 0;
  const brandColors = hasParentBrandColors ? parentGeneratedBrandColors : localBrandColors;
  const primaryColor = hasParentBrandColors
    ? parentGeneratedBrandColors['accent-50'] || localPrimaryColor
    : localPrimaryColor;
  const [primaryImageError, setPrimaryImageError] = useState(false);
  const persistedImageUrl = imageUrl({
    base: getGlobalImageUrl(),
    type: data.elementType === 'EVENT' ? 'event' : 'fundraiser',
    name: `${data.nonprofitName}_${data.formID}`,
    version: imageVersionFor(data),
  });
  const [imageReady, setImageReady] = useState(false);

  // Extract brand color for dynamic SVG fallback
  const brandColor = primaryColor || lightColor || data?.lightColor;
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);
  // The card never grows: the full description and the FAQs open in a sheet over it
  // (DetailLearnMore), so it always keeps its fixed height and ends on Give.
  const bodyRef = useRef(null);
  const descriptionSlotRef = useRef(null);
  // Detail pages use CSS container-query-based fixed height (dynamic-detail-height class)
  // in non-drawer mode. Height scales: <320px=460px, 320-448px=520px, 449+=560px.
  const useFixedHeight = !isDrawerOpen;
  // Keep column flex on the frosted shell while expanded so closing FAQ does not
  // toggle layout mode (block → flex), which reflowed the white card and made the
  // Support/FAQs footer jump. Only gate overflow:hidden to collapsed so expanded
  // content can take its natural height without resizing the shell frame-by-frame.
  const fixedDetailColumnStyles = useFixedHeight
    ? { display: 'flex', flexDirection: 'column' }
    : {};
  const fixedDetailOverflowHidden = useFixedHeight
    ? { overflow: 'hidden' }
    : {};
  const fixedDetailBodyStyles = useFixedHeight
    ? { flex: '1 1 auto', minHeight: 0 }
    : {};

  // Detect if the body content overflows the fixed-height container.
  // Skip re-measurement both when expanded AND on the collapse transition,
  // because the container hasn't settled to its constrained height yet during
  // collapse — scrollHeight === clientHeight would incorrectly report no overflow.
  useLayoutEffect(() => {
    if (!bodyRef.current || isDrawerOpen) return;
    const el = bodyRef.current;
    setIsDescriptionOverflowing(el.scrollHeight > el.clientHeight);
  }, [data?.details, data?.showFundraisingBar, isDrawerOpen]);

  useEffect(() => {
    if (data?.lightColor) {
      setLightColor(data.lightColor); // Direct color extraction
    }
  }, [data?.lightColor]);

  const handleContinue = () => {
    if (paymentsUnavailable) return;
    setPage('FORM', 'forward');
  };

  // Reset error and loading states when image changes in preview mode
  useEffect(() => {
    if (previewMode && data?.image !== undefined) {
      setPrimaryImageError(false);
      setImageReady(false);
    }
  }, [previewMode, data?.image]);

  // FallbackHeaderImage renders inline SVG — no async load, mark ready immediately.
  // One failed load is enough: a second attempt would request the same stable
  // versioned URL, so React would keep the same <img>, nothing would be
  // re-requested, no second error would fire, and a form with no photo would sit
  // on a broken image. (The old retry only worked while the URL carried a
  // per-render timestamp.)
  const usesFallback = (previewMode && !data?.image) || primaryImageError;
  useEffect(() => {
    if (usesFallback) {
      setImageReady(true);
    }
  }, [usesFallback]);

  return (
    <div
      className={`${isDrawerOpen || isOverlay ? '' : 'container'} ${isDrawerOpen || isOverlay ? '' : 'mx-auto'} ${isOverlay ? 'flex-1 items-stretch' : 'h-full items-center'} ${
        isDrawerOpen ? 'w-full px-4' : isOverlay ? 'w-full flex flex-col' : 'max-w-md'
      }`}
      style={{
        minHeight: isDrawerOpen ? '100%' : isOverlay ? undefined : 'fit-content',
        height: isDrawerOpen ? '100%' : isOverlay ? undefined : 'auto',
      }}
    >
      <div
        className={`${
          isOverlay
            ? ''
            : isHostedPage && !isDrawerOpen
              ? 'hosted-spacing-wrapper'
              : !isHostedPage
                ? 'dynamic-spacing-wrapper'
                : ''
        } ${isOverlay ? 'flex-1 flex flex-col' : ''}`}
        style={{
          ...(!isHostedPage || isDrawerOpen
            ? { padding: isDrawerOpen ? '16px 0' : isOverlay ? '0px 0' : '24px 0' }
            : {}),
          margin: '0',
          height: isDrawerOpen ? '100%' : useFixedHeight ? 'auto' : 'fit-content',
          boxSizing: 'border-box',
        }}
      >
        <div
          // The FAQs sheet (DetailMoreRow) opens over this card: it is the drawer's scope.
          data-pane-sheet-scope=""
          className={`relative overflow-hidden ${!isOverlay ? 'frosted-background' : ''} ${!isOverlay && !isDrawerOpen ? 'cs-form-card' : ''} ${!isDrawerOpen && !isHostedPage && !isOverlay ? 'dynamic-frosted-margin' : ''} ${isOverlay ? 'flex-1 flex flex-col' : ''} ${useFixedHeight ? 'dynamic-detail-height' : ''}`}
          style={{
            ...fixedDetailColumnStyles,
            ...fixedDetailOverflowHidden,
            ...(isDrawerOpen && {
              margin: '0',
              padding: '10px',
              borderRadius: '0',
            }),
            ...(isOverlay && {
              borderRadius: '0',
              boxShadow: 'none',
            }),
          }}
        >
          {/* Header image — flush to the card's edge, top corners on the card's
              inner curve (Paper 7VQ-0). The overlay pane draws its own edge. */}
          <div
            className="shrink-0 overflow-hidden cs-form-card-media bg-[color:var(--cs-surface-0)]"
            style={
              isOverlay
                ? { border: 'var(--cs-border-width) solid var(--cs-border-control)', borderBottom: 'none' }
                : undefined
            }
          >
            {/* Skeleton shimmer — visible until image is fully loaded */}
            {!imageReady && (
              <div className="aspect-video w-full bg-[color:var(--cs-surface-sunken)] skeleton-item skeleton-wave" />
            )}
            {/* Image renders invisibly until ready, then swaps in all at once */}
            <div className="aspect-video w-full relative" style={!imageReady ? { position: 'absolute', top: 0, left: 0, right: 0, opacity: 0, pointerEvents: 'none' } : undefined}>
              {previewMode && !data.image ? (
                <FallbackHeaderImage
                  alt="Default fundraiser image: heart-shaped forest"
                  variant="donation-elements"
                  brandColor={brandColor}
                  title={data.formName}
                />
              ) : !primaryImageError ? (
                <img
                  src={
                    previewMode
                      ? data.image
                      : persistedImageUrl
                  }
                  className="absolute inset-0 w-full h-full object-cover bg-[color:var(--cs-placeholder)]"
                  alt="Fundraiser Header"
                  onLoad={() => setImageReady(true)}
                  onError={() => setPrimaryImageError(true)}
                  loading="eager"
                  key={previewMode ? data.image : undefined}
                />
              ) : (
                <FallbackHeaderImage
                  alt="Default fundraiser image: heart-shaped forest"
                  variant="donation-elements"
                  brandColor={brandColor}
                  title={data.formName}
                />
              )}
            </div>
          </div>

          {/* Content Container - Normal Padding. It slides when the detail page is switched to
              (SURFACE_CONTENT_CLASS); the card and its image hold still. */}
          <div
            className={`${SURFACE_CONTENT_CLASS} flex flex-col ${
              isOverlay
                ? 'flex-1 bg-white rounded-b-md dynamic-content-padding detail-content-reduced-top'
                : 'cs-form-card-body'
            }`}
            style={{
              ...(isOverlay && {
                border: 'var(--cs-border-width) solid var(--cs-border-control)',
                borderTop: 'none',
              }),
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'hidden',
              ...(isOverlay && {
                paddingLeft: '1rem',
                paddingRight: '1rem',
              }),
            }}
          >
            {/* Title + description + Read more. Footer stays pinned inside white so FAQ/Support never paint below the card. */}
            <div className="detail-description-clip-area min-h-0 flex-1 flex flex-col">
            <div
              ref={descriptionSlotRef}
              style={{
                position: 'relative',
                flex: '1 1 auto',
                minHeight: 0,
              }}
            >
              <div
                ref={bodyRef}
                style={{
                  ...fixedDetailBodyStyles,
                  // The card clips the description; the full text is in the Learn more sheet.
                  overflow: 'hidden',
                  height: '100%',
                  ...(isOverlay && { display: 'flex', flexDirection: 'column' }),
                }}
              >
                {/* Form Title */}
                <div>
                  <HeadingTag className="m-0 font-sans text-[length:var(--cs-text-2xl)] leading-[var(--cs-leading-30)] font-semibold tracking-[var(--cs-tracking-tight)] text-[color:var(--cs-text-primary)]">
                    {data.formName}
                  </HeadingTag>
                </div>

                {/* Fundraising Bar */}
                {data.showFundraisingBar && (
                  <div className="mt-[var(--cs-space-4)]">
                    <FundraisingBar
                      fundraisingBarID={
                        data.fundraisingBarElementID
                          ? data.fundraisingBarElementID
                          : previewMode
                            ? 'NEW_FORM'
                            : ''
                      }
                      brandColors={brandColors}
                      previewMode={previewMode}
                      compact
                      amounts={{
                        targetAmount: data.targetAmount,
                        raisedAmount:
                          (parseInt(data.totalTransactionAmount) || 0) +
                          (parseInt(data.amountOffset) || 0),
                        trackingMetric: data.trackingMetric,
                        metricLabel: data.metricLabel,
                        elementOffset: data.elementOffset || 0,
                      }}
                    />
                  </div>
                )}

                {/* Form Description (DetailDescription: the same render as the Learn more sheet) */}
                <DetailDescription
                  html={data.details}
                  className="mt-[var(--cs-space-4)]"
                  style={isOverlay ? { overflowY: 'hidden', flex: '1 1 auto', minHeight: 0 } : undefined}
                />
              </div>
              {/* Fade gradient to mask half-cut lines when description overflows */}
              {isDescriptionOverflowing && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '24px',
                    background: 'linear-gradient(to top, white, transparent)',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>

            {/* Learn more (full description + FAQs in a sheet) or FAQs, above Give (DetailLearnMore) */}
            {!isDrawerOpen && (
              <div
                className="flex-shrink-0"
                style={{ marginTop: '0rem' }}
              >
                <DetailLearnMore
                  title={data.formName}
                  descriptionHtml={data.details}
                  isDescriptionClipped={isDescriptionOverflowing}
                  faqs={data.formFaqs}
                  elementID={data.elementID || data.formID}
                />
              </div>
            )}
            </div>

            {/* Give — the card ends on its primary button; FAQs sit on the Read more row above */}
            <div className="flex-shrink-0 mt-auto flex flex-col gap-2 pt-3 form-action-buttons">
              {paymentsUnavailable && (
                <p role="status" className="text-sm text-center text-[color:var(--cs-text-secondary)]">
                  Payments are temporarily unavailable. Please try again later.
                </p>
              )}
              {!isOverlay && (
                <div className="relative overflow-hidden">
                  <Button
                    type="button"
                    family="cta"
                    variant="cta"
                    size="cta"
                    className="dynamic-button-text"
                    onClick={handleContinue}
                    disabled={paymentsUnavailable}
                  >
                    {/* Opens the form, like the sticky bars: "Give" (Paper 4NE-0 naming rule, CON-571). */}
                    Give
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmbedFormDetailPage({
  data,
  setPage,
  generatedBrandColors,
  isDrawerOpen,
  elementID,
  domID,
  previewMode = false,
  formDisplayMode,
  headingLevel = 'h1',
}) {
  return (
    <EmbedFormDetailPageContent
      data={data}
      setPage={setPage}
      generatedBrandColors={generatedBrandColors}
      isDrawerOpen={isDrawerOpen}
      previewMode={previewMode}
      formDisplayMode={formDisplayMode}
      headingLevel={headingLevel}
    />
  );
}

export default EmbedFormDetailPage;
